import Geocode from 'react-geocode'
import { ApiKeyMaps } from './constants'

Geocode.setApiKey(ApiKeyMaps)
Geocode.setLanguage('en')
/*
 Google's location_type says how tight the match is, so the map can open close
 when the point is a real address and stay wide when it only resolved a region.
*/
const PRECISION_ZOOM = {
  ROOFTOP: 17,
  RANGE_INTERPOLATED: 16,
  GEOMETRIC_CENTER: 15,
  APPROXIMATE: 11,
}

export const zoomForPrecision = (precision) => PRECISION_ZOOM[precision] ?? 11

/* A partial match or an APPROXIMATE point means the address was not fully
   resolved, so the form warns instead of showing it as an exact location. */
export const isApproximate = (direction) =>
  Boolean(direction?.partial) || direction?.precision === 'APPROXIMATE'

/*
 The city catalog usually names its rows "<zip code>. <name>", but not always,
 so the zip code is dropped only when the row really starts with one. Splitting
 on the first dot unconditionally returned undefined for a plain name such as
 "Guyane", and that undefined used to reach google as a literal word.
*/
const cityLabel = (name) => {
  const raw = String(name ?? '').trim()
  const withZipCode = raw.match(/^\d+\s*\.\s*(.+)$/)
  return (withZipCode ? withZipCode[1] : raw).trim()
}

/*
 Builds the query google is asked for. Department and community are optional and
 only exist for France, and any of the other parts can be missing too, so empty
 pieces are dropped instead of being interpolated. Extra parts are safe: google
 weighs the components, so a precise street address still wins over them.
*/
export const buildAddressQuery = ({
  address,
  community,
  state,
  city,
  country,
}) =>
  [address, community, state, cityLabel(city), country]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(', ')

/*
 Returns the coordinates plus the metadata google already sends about how sure
 it is: precision is the location_type (ROOFTOP, RANGE_INTERPOLATED,
 GEOMETRIC_CENTER, APPROXIMATE) and partial flags a partial_match. The map uses
 both to pick its zoom and to warn when the point is only approximate.
*/
export const getLatAndLong = async (address) => {
  try {
    const response = await Geocode.fromAddress(address)
    const result = response.results?.[0]
    if (!result) return null
    const { lat, lng } = result.geometry.location
    return {
      lat: lat,
      lng: lng,
      precision: result.geometry.location_type,
      partial: result.partial_match === true,
      formattedAddress: result.formatted_address,
    }
  } catch {
    /*
     react-geocode rejects on any status other than OK, and ZERO_RESULTS is an
     ordinary answer for an address still being typed. Returning null lets the
     caller keep the point it already had instead of leaving the rejection
     unhandled.
    */
    return null
  }
}
